import React from 'react';
import { 
  Title3,
  Body2,
  Divider,
} from "@fluentui/react-components";
import { 
  Globe16Regular,
  Open16Filled,
  Cloud16Filled,
  Open16Regular,
  BrainCircuitFilled,
  CloudDatabaseFilled,
  PuzzleCube16Filled,
  AnimalDog16Regular,
  BotSparkle16Filled,
  Rocket16Regular
} from "@fluentui/react-icons";
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import WarningCard from './WarningCard';
import PageHeader from './PageHeader';
import QuickStartTable from './QuickStartTable';
import NeedHelpSection from './NeedHelpSection';
import TipsSection from './TipsSection';
import ServiceInfoCard from './ServiceInfoCard';
import { FEATURE_FLAGS } from '../../../config/featureFlags';

export interface ServiceInfo {
    id: string;
    name: string;
    description: string;
    icon: React.ReactElement,
    steps: string[];
    url: string;
    pricingUrl: string;
    baseUrl: string;
    note: string;
}

const InfoPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();

  React.useEffect(() => {
    document.title = t('info.title', 'API Setup Guide');
  }, []);

  const services : ServiceInfo[] = [
    {
      id: 'openai',
      name: t('info.services.openai.name', 'OpenAI'),
      description: t('info.services.openai.description', 'OpenAI\'s official API service with GPT-4, GPT-3.5, and other models'),
      icon: <BrainCircuitFilled />,
      steps: [
        t('info.services.openai.steps.1', 'Go to platform.openai.com and sign up/login'),
        t('info.services.openai.steps.2', 'Click on your profile → "View API keys"'),
        t('info.services.openai.steps.3', 'Click "Create new secret key"'),
        t('info.services.openai.steps.4', 'Copy the key (you won\'t see it again!)'),
        t('info.services.openai.steps.5', 'Paste it in the settings page of this add-in')
      ],
      url: 'https://platform.openai.com/api-keys',
      pricingUrl: 'https://openai.com/pricing',
      baseUrl: 'https://api.openai.com/v1',
      note: t('info.services.openai.note', 'Keys start with "sk-"')
    },
    {
      id: 'azure',
      name: t('info.services.azure.name', 'Azure OpenAI'),
      description: t('info.services.azure.description', 'Microsoft Azure\'s OpenAI service with enterprise features'),
      icon: <CloudDatabaseFilled />,
      steps: [
        t('info.services.azure.steps.1', 'Have an Azure subscription (or create a free account)'),
        t('info.services.azure.steps.2', 'Create an "Azure OpenAI" resource in the Azure portal'),
        t('info.services.azure.steps.3', 'Deploy a model (GPT-4, GPT-3.5) in your resource'),
        t('info.services.azure.steps.4', 'Go to "Keys and Endpoint" in your resource'),
        t('info.services.azure.steps.5', 'Copy one of the keys and the endpoint URL'),
        t('info.services.azure.steps.6', 'Paste both in the settings page of this add-in')
      ],
      url: 'https://portal.azure.com',
      pricingUrl: 'https://azure.microsoft.com/pricing/details/cognitive-services/openai-service/',
      baseUrl: t('info.services.azure.baseUrl', 'Your endpoint URL from Azure'),
      note: t('info.services.azure.note', 'Requires Azure subscription and resource deployment')
    },
    {
      id: 'compatible',
      name: t('info.services.compatible.name', 'OpenAI-Compatible Services'),
      description: t('info.services.compatible.description', 'Services that use the OpenAI API format (Anthropic, Together AI, Local LLMs, etc.)'),
      icon: <PuzzleCube16Filled />,
      steps: [
        t('info.services.compatible.steps.1', 'Sign up for an OpenAI-compatible service'),
        t('info.services.compatible.steps.2', 'Obtain your API key from their dashboard'),
        t('info.services.compatible.steps.3', 'Get their API endpoint URL'),
        t('info.services.compatible.steps.4', 'Paste both key and URL in the settings page'),
        t('info.services.compatible.steps.5', 'Ensure it supports the OpenAI API format')
      ],
      url: '',
      pricingUrl: '',
      baseUrl: t('info.services.compatible.baseUrl', 'Varies by service provider'),
      note: t('info.services.compatible.note', 'Must support OpenAI API format')
    },
    {
      id: 'anthropic',
      name: t('info.services.anthropic.name', 'Anthropic (Claude)'),
      description: t('info.services.anthropic.description', 'Anthropic\'s official Claude API with advanced reasoning and analysis models'),
      icon: <AnimalDog16Regular />,
      steps: [
        t('info.services.anthropic.steps.1', 'Go to console.anthropic.com and sign up/login'),
        t('info.services.anthropic.steps.2', 'Navigate to "API Keys" and click "Create Key"'),
        t('info.services.anthropic.steps.3', 'Copy the key (starts with "sk-ant-")'),
        t('info.services.anthropic.steps.4', 'Paste it in the settings page of this add-in'),
        t('info.services.anthropic.steps.5', 'Select "Anthropic (Claude)" as your AI service')
      ],
      url: 'https://console.anthropic.com/settings/keys',
      pricingUrl: 'https://www.anthropic.com/pricing',
      baseUrl: 'https://api.anthropic.com/v1',
      note: t('info.services.anthropic.note', 'Keys start with "sk-ant-". Default URL is used automatically.')
    },
    {
      id: 'bedrock',
      name: t('info.services.bedrock.name', 'Amazon Bedrock'),
      description: t('info.services.bedrock.description', 'AWS foundation models (Claude, Llama, Titan, Nova) via the Bedrock Mantle OpenAI-compatible endpoint'),
      icon: <Cloud16Filled />,
      steps: [
        t('info.services.bedrock.steps.1', 'Sign in to the AWS Management Console'),
        t('info.services.bedrock.steps.2', 'Enable access to foundation models in Amazon Bedrock'),
        t('info.services.bedrock.steps.3', 'Obtain AWS credentials (access key/secret) or an API key'),
        t('info.services.bedrock.steps.4', 'Determine your region and build the Mantle endpoint URL'),
        t('info.services.bedrock.steps.5', 'Paste the endpoint URL and credentials in the settings page')
      ],
      url: 'https://us-east-1.console.aws.amazon.com/bedrock',
      pricingUrl: 'https://aws.amazon.com/bedrock/pricing/',
      baseUrl: 'https://bedrock-mantle.{region}.api.aws',
      note: t('info.services.bedrock.note', 'Uses the OpenAI-compatible Chat Completions format via Bedrock Mantle')
    },
    {
      id: 'bedrock-agentcore',
      name: t('info.services.bedrockAgentCore.name', 'Amazon Bedrock AgentCore'),
      description: t('info.services.bedrockAgentCore.description', 'AWS managed agent runtime for deploying AI agents with tools, memory, and streaming responses'),
      icon: <Rocket16Regular />,
      steps: [
        t('info.services.bedrockAgentCore.steps.1', 'Sign in to the AWS Management Console'),
        t('info.services.bedrockAgentCore.steps.2', 'Deploy an agent using Amazon Bedrock AgentCore Runtime'),
        t('info.services.bedrockAgentCore.steps.3', 'Copy the agent endpoint URL (agent ARN)'),
        t('info.services.bedrockAgentCore.steps.4', 'Generate an auth token or configure IAM access'),
        t('info.services.bedrockAgentCore.steps.5', 'Paste the endpoint URL and token in the settings page')
      ],
      url: 'https://docs.aws.amazon.com/bedrock-agentcore/',
      pricingUrl: 'https://aws.amazon.com/bedrock/pricing/',
      baseUrl: t('info.services.bedrockAgentCore.baseUrl', 'Your AgentCore Runtime agent endpoint URL'),
      note: t('info.services.bedrockAgentCore.note', 'Runs a full agent — the agent handles tools, memory, and multi-step reasoning internally')
    }
  ];

  const visibleServices = services.filter((service) => {
    switch (service.id) {
      case 'anthropic':
        return FEATURE_FLAGS.anthropic;
      case 'bedrock':
        return FEATURE_FLAGS.bedrock;
      case 'bedrock-agentcore':
        return FEATURE_FLAGS.bedrockAgentCore;
      default:
        return true;
    }
  });

  return (
    <div className={styles.container}>
      <PageHeader styles={styles} />

      <Divider className={styles.divider} />

      {/* Important Warning Card */}
      <WarningCard styles={styles} />

      {/* Quick Start Table */}
      <QuickStartTable styles={styles}/>

      {/* Service-Specific Instructions */}
      <div className={styles.section}>
        <Title3>
          <Globe16Regular className={styles.sectionIcon} />
          {t('info.services.title', 'Service-Specific Setup')}
        </Title3> 
        <br />
        <Body2 className={styles.sectionDescription}>
          {t('info.services.description', 'Select your preferred service for detailed setup instructions:')}
        </Body2>

        <div className={styles.servicesGrid}>
          {visibleServices.map((service) => (
            <ServiceInfoCard key={service.id} service={service} styles={styles}/>
          ))}
        </div>
      </div>

      {/* Tips & Best Practices */}
      <TipsSection styles={styles}/>

      {/* Need Help Section */}
      <NeedHelpSection styles={styles}/>
    </div>
  );
};

export default InfoPage;